import { FocusMonitor } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewEncapsulation, Input } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

// Boilerplate for applying mixins to MatButton.

/**
 * Material design button.
 */

@Component({
  selector: `button[bll-button], button[bll-icon-button]`,
  exportAs: 'bllButton',
  host: {
    '[class.bll-btn]': 'true',
    '[class.bll-btn-primary]': '!outline && !flat',
    '[class.bll-btn-outline]': 'outline',
    '[class.bll-btn-flat]': 'flat',
    '[class.bll-btn-expand]': 'expand',
    '[class.bll-btn-icon]': 'icon',
    '[attr.disabled]': 'disabled || null',
  },
  templateUrl: 'button.component.html',
  styleUrls: ['button.component.scss'],
  // encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class bllButton implements OnDestroy {
  /** Whether the button is icon button. */

  constructor(private _elementRef: ElementRef, private _focusMonitor: FocusMonitor) {
    this._focusMonitor.monitor(this._elementRef, true);
  }

  @Input()
  get disabled(): any {
    return this._disabled;
  }
  set disabled(value: any) {
    this._disabled = coerceBooleanProperty(value);
  }
  private _disabled: boolean;

  @Input()
  get outline(): any {
    return this._outline;
  }
  set outline(value: any) {
    this._outline = coerceBooleanProperty(value);
  }
  private _outline: boolean;

  @Input()
  get flat(): any {
    return this._flat;
  }
  set flat(value: any) {
    this._flat = coerceBooleanProperty(value);
  }
  private _flat: boolean;

  @Input()
  get expand(): any {
    return this._expand;
  }
  set expand(value: any) {
    this._expand = coerceBooleanProperty(value);
  }
  private _expand: boolean;

  @Input()
  get icon(): string {
    return this._icon;
  }
  set icon(value: string) {
    this._icon = value;
  }
  private _icon: string;

  ngOnDestroy() {
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  /** Focuses the button. */
  focus(): void {
    this._getHostElement().focus();
  }

  _getHostElement() {
    return this._elementRef.nativeElement;
  }
}

/**
 * Material design anchor button.
 */
@Component({
  selector: `a[bll-button], a[bll-icon-button]`,
  exportAs: 'bllButton, bllAnchor',
  host: {
    // Note that we ignore the user-specified tabindex when it's disabled for
    // consistency with the `bll-button` applied on native buttons where even
    // though they have an index, they're not tabbable.
    '[class.bll-btn]': 'true',
    '[attr.tabindex]': 'disabled ? -1 : (tabIndex || 0)',
    '[attr.disabled]': 'disabled || null',
    '[attr.aria-disabled]': 'disabled ? disabled.toString() : "false"',
    '(click)': '_haltDisabledEvents($event)',
  },
  inputs: ['disableRipple', 'color'],
  templateUrl: 'button.component.html',
  styleUrls: ['button.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class uiAnchor extends bllButton {
  /** Tabindex of the button. */
  @Input() tabIndex: number;
  constructor(focusMonitor: FocusMonitor, elementRef: ElementRef) {
    super(elementRef, focusMonitor);
  }

  _haltDisabledEvents(event: Event) {
    // A disabled button shouldn't apply any actions
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}
